'use client';

import { DuoColorIcon, Paragraph, Text } from '@zillow/constellation-10';
import { Bird, Cat, Dog, Fish, PawPrint, Rabbit, Turtle } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import type React from 'react';
import { LucideIcon, type LucideIconComponent } from '@/src/components/LucideIcon';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { Module } from '@/src/components/features/property/ui/Module/Module';
import { ReadMoreText } from '@/src/components/features/property/lib/ReadMoreText';
import {
  buildPetRows,
  formatCount,
  formatFeeAmount,
  formatWeight,
  hoistSharedNotes,
  type PetKind,
  type PetPolicyMap,
  type PetRow,
  type PetSize,
  type SharedPetNotes,
} from './petRows';
import { petHeadingText, petIcon, petList, petNotes, petRow, petRowBody, petRowHeading } from './petsModule.styles';

type HdpT = ReturnType<typeof useTranslations<'hdp'>>;
type Status = 'allowed' | 'notAllowed' | 'notStated';

const KIND_ICON: Record<PetKind, LucideIconComponent> = {
  all: PawPrint,
  dog: Dog,
  cat: Cat,
  bird: Bird,
  fish: Fish,
  reptile: Turtle,
  other: Rabbit,
};

const KIND_LABEL = {
  all: 'pets',
  dog: 'dogs',
  cat: 'cats',
  bird: 'birds',
  fish: 'fish',
  reptile: 'reptiles',
  other: 'otherPets',
} as const satisfies Record<PetKind, string>;

const KIND_NOUN = {
  all: 'petsNoun',
  dog: 'dogsNoun',
  cat: 'catsNoun',
  bird: 'birdsNoun',
  fish: 'fishNoun',
  reptile: 'reptilesNoun',
  other: 'otherPetsNoun',
} as const satisfies Record<PetKind, string>;

const SIZE_WORD = {
  tiny: 'petSizeTiny',
  small: 'petSizeSmall',
  medium: 'petSizeMedium',
  large: 'petSizeLarge',
  huge: 'petSizeHuge',
} as const satisfies Record<PetSize, string>;

const STATUS_TONE = { allowed: 'success', notAllowed: 'critical', notStated: 'info' } as const satisfies Record<
  Status,
  string
>;

const statusOf = (allowed: boolean | null): Status =>
  allowed === null ? 'notStated' : allowed ? 'allowed' : 'notAllowed';

function rowLabel(row: PetRow, t: HdpT, locale: string): string {
  if (row.sizes.length === 0) return t(KIND_LABEL[row.kind]);
  const sizes = new Intl.ListFormat(locale, { type: 'conjunction' }).format(
    row.sizes.map((size) => t(SIZE_WORD[size])),
  );
  // The locale owns the word order: "{sizes} {pets}" in English, "{pets} {sizes}" in Spanish.
  const label = t('sizedPets', { sizes, pets: t(KIND_NOUN[row.kind]) });
  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
}

function limitsText(row: PetRow, t: HdpT): string {
  return [
    row.maxNumberAllowed && t('petsUpTo', { count: formatCount(row.maxNumberAllowed) }),
    row.maxWeightAllowed && t('petsWeightMax', { weight: formatWeight(row.maxWeightAllowed) }),
  ]
    .filter(Boolean)
    .join(' · ');
}

function feesText(row: PetRow, t: HdpT): string {
  return [
    row.deposit && t('petDepositValue', { amount: formatFeeAmount(row.deposit) }),
    row.oneTimeFee && t('petOneTimeFeeValue', { amount: formatFeeAmount(row.oneTimeFee) }),
    row.monthlyFee && t('monthlyFeeValue', { value: formatFeeAmount(row.monthlyFee) }),
  ]
    .filter(Boolean)
    .join(' · ');
}

type PetRowItemProps = { row: PetRow };

const PetRowItem: React.FC<PetRowItemProps> = ({ row }) => {
  const t = useTranslations('hdp');
  const locale = useLocale();
  const status = statusOf(row.allowed);
  const label = rowLabel(row, t, locale);
  // A pet that is not allowed has no fees or limits to show; its notes still apply.
  const limits = row.allowed === false ? '' : limitsText(row, t);
  const fees = row.allowed === false ? '' : feesText(row, t);

  return (
    <li className={petRow}>
      <DuoColorIcon tone={STATUS_TONE[status]} css={petIcon} aria-hidden="true">
        <LucideIcon icon={KIND_ICON[row.kind]} />
      </DuoColorIcon>
      <div className={petRowBody}>
        <div className={petRowHeading}>
          <Text textStyle="body-bold" css={petHeadingText}>
            {t('petStatusName', { pets: label, status: t('petStatus', { status }) })}
          </Text>
          {limits && (
            <Text textStyle="body-sm" color="text.subtle">
              {limits}
            </Text>
          )}
        </div>
        {fees && <Paragraph textStyle="body-sm">{fees}</Paragraph>}
        {row.comment && <PetComment idPrefix={row.key} comment={row.comment} />}
        <PetNotes idPrefix={row.key} notes={{ restriction: row.restriction, comment: null }} />
      </div>
    </li>
  );
};

type PetCommentProps = { idPrefix: string; comment: string };

// ShowHide.Root (ReadMoreText) renders a div, which a Paragraph's <p> cannot hold.
const PetComment: React.FC<PetCommentProps> = ({ idPrefix, comment }) => {
  const t = useTranslations('hdp');

  return (
    <Text asChild textStyle="body-sm">
      <div>
        <ReadMoreText
          controlsId={`${idPrefix}-pet-policy-additional-info`}
          content={comment}
          maxTruncatedLength={100}
          readMoreText={t('readMore')}
          readLessText={t('readLess')}
          aria-label={t('petPolicyAdditional')}
        />
      </div>
    </Text>
  );
};

type PetNotesProps = { idPrefix: string; notes: SharedPetNotes };

const PetNotes: React.FC<PetNotesProps> = ({ idPrefix, notes: { restriction, comment } }) => {
  const t = useTranslations('hdp');

  if (!restriction && !comment) {
    return null;
  }

  return (
    <div className={petNotes}>
      {restriction && (
        <Text asChild textStyle="body-sm">
          <div>
            <Text textStyle="body-sm-bold">{t('restrictions')}: </Text>
            <ReadMoreText
              controlsId={`${idPrefix}-pet-policy-restrictions`}
              content={restriction}
              maxTruncatedLength={100}
              readMoreText={t('readMore')}
              readLessText={t('readLess')}
              aria-label={t('petPolicyRestrictions')}
            />
          </div>
        </Text>
      )}
      {comment && <PetComment idPrefix={idPrefix} comment={comment} />}
    </div>
  );
};

type Props = {
  listing: PropertyDetailListing;
};

export const PetsModule: React.FC<Props> = ({ listing }) => {
  const t = useTranslations('hdp');
  const { rows, shared } = hoistSharedNotes(
    buildPetRows((listing?.amenities?.petPolicies ?? null) as PetPolicyMap | null),
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <Module id="pets-module" title={t('petsTitle')}>
      <ul className={petList}>
        {rows.map((row) => (
          <PetRowItem key={row.key} row={row} />
        ))}
      </ul>
      <PetNotes idPrefix="pets-shared" notes={shared} />
    </Module>
  );
};
